<script>
  import * as Pancake from "@sveltejs/pancake";
  import { tweened } from "svelte/motion";
  import * as eases from "svelte/easing";
  import { fade } from "svelte/transition";
  import PancakeChart from "./Pancake.svelte";
  import { treemap, hierarchy } from "d3-hierarchy";
  import {
    budgetBlocksTreeDepts,
    actualSelectedBudgetTreeDepts,
    selectedWithHighestUnselected,
    otherDeptsGrouped,
    explore,
  } from "@stores/treemap.js";
  import colorScale from "@helpers/departmentTypeColorScale.js";
  import DepartmentTypeLegend from "@helpers/DepartmentTypeLegend.svelte";
  import ShareButton from "./ShareButton.svelte";

  export let step;

  $: displayData = {
    name: "Houston Budget",
    level: "root",
    children: $budgetBlocksTreeDepts,
  };

  $: {
    if (step === 0 || step === undefined) {
      displayData.children = $budgetBlocksTreeDepts;
    }

    if (step === 1) {
      // Only show selected departments in treemap
      // sizing using actual dept budgets from fy2022
      displayData.children = $actualSelectedBudgetTreeDepts;
    }
    if (step === 2) {
      // add highest unselected budget department
      displayData.children = $selectedWithHighestUnselected;
    }
    if (step === 3) {
      // Show selected departments, and create a new huge block representing the sum of all other departments
      displayData.children = $otherDeptsGrouped;
      // displayData = groupedData;
    }
    if (step === 4 || step == 5) {
      // Unnest the unselected block and show all departments inside
      displayData.children = $explore;
    }
  }

  $: treemapper = treemap();

  $: hierarch = hierarchy(displayData).sum((d) => d.value);
  $: hierarch.eachBefore((node) => {
    if (!node.parent) {
      return;
    }
    if (node.parent.data.isSelected !== undefined) {
      node.data.isSelected = node.parent.data.isSelected;
    }
    if (node.parent.data.departmentType !== undefined) {
      node.data.departmentType = node.parent.data.departmentType;
    }
  });
  $: hierarch.eachAfter((node) => {
    if (node.children) {
      node.data.amount = node.children.reduce(
        (prev, child) => prev + (child.data.amount || child.value),
        0
      );
    }
  });
  $: hierarch.sort((a, b) => {
    return b.data.amount - a.data.amount;
  });

  $: root = treemapper(hierarch);

  $: selected = root;

  $: select = (node) => {
    if (node.data.level !== "department") {
      while (node.parent && node.parent !== selected) {
        node = node.parent;
      }
    }

    if (node && node.children) selected = node;
  };

  $: breadcrumbs = (node) => {
    const crumbs = [];
    while (node) {
      crumbs.unshift(node.data.name);
      node = node.parent;
    }

    return crumbs.join(" &rarr; ");
  };

  $: extents = tweened(undefined, {
    easing: eases.cubicOut,
    duration: 600,
  });

  $: is_visible = (a, b) => {
    // show only department level until step 4
    // show only department level at root of step 4; hide department type nodes
    if (step !== 4 || b.depth === 0) {
      return a.data.level === "department";
    }
    while (b) {
      if (a.parent === b) {
        return true;
      }
      b = b.parent;
    }

    return false;
  };

  $: $extents = {
    x1: selected.x0,
    x2: selected.x1,
    y1: selected.y1,
    y2: selected.y0,
  };

  import { format } from "d3-format";
  const formatNum = (num) => format(",.2~f")(num);

  // Lookup node size and return true if width or height is less than .1
  $: isSmall = (node) => {
    // Update this whenever the user zooms in to a new level
    // We do this via the extents object, where extents.x2 and extents.y2 represent bounds
    const treemapWidth = $extents.x2 - $extents.x1;
    const nodeWidth = node.x1 - node.x0;
    const width = nodeWidth / treemapWidth;

    const treemapHeight = $extents.y1 - $extents.y2;
    const nodeHeight = node.y1 - node.y0;
    const height = nodeHeight / treemapHeight;

    return width < 0.1 || height < 0.1;
  };
</script>

<div class="treemap" id="treemap">
  <button
    class="breadcrumbs"
    class:clickable={selected.parent}
    disabled={!selected.parent}
    on:click={() => (selected = selected.parent)}
  >
    {@html breadcrumbs(selected)}
  </button>

  <div class="chart">
    <Pancake.Chart
      x1={$extents.x1}
      x2={$extents.x2}
      y1={$extents.y1}
      y2={$extents.y2}
    >
      <PancakeChart {root} let:node>
        {#if is_visible(node, selected)}
          <div
            transition:fade={{ duration: 400 }}
            class="node {node.data.level}"
            class:leaf={!node.children}
            class:has_children={node.children}
            on:click={() => {
              (step === 4 || step === 5) && select(node);
            }}
          >
            <div
              class="contents {!node.data.isSelected
                ? 'inactive'
                : 'active'} {((step === 4 || step === 5) && 'explore') ||
                'scrolly'}"
              class:small={isSmall(node)}
              style="background-color: {colorScale(node.data.departmentType)}"
            >
              <h1>{node.data.name}</h1>
              <h2>
                {(node.data.mode === "budget-blocks" && `${node.value}/10`) ||
                  `$${formatNum(node.value)}`}
              </h2>
            </div>
          </div>
        {/if}
      </PancakeChart>
    </Pancake.Chart>
  </div>

  {#if step === 4 || step === 5}
    <div class="flex">
      <DepartmentTypeLegend />
      <ShareButton />
    </div>
  {/if}
</div>

<style>
  .treemap {
    text-align: left;
    /* padding: 1rem; */
    display: flex;
    flex-direction: column;
    justify-content: center;
    place-items: center;
    width: 100%;
    position: sticky;
    top: var(--nav-height);
  }

  .chart {
    width: 95%;
    margin: 0 auto;
    height: calc(85vh - var(--nav-height));
    margin-bottom: 0.75rem;
    padding: 0;
    overflow: hidden;
  }

  .breadcrumbs {
    width: 95%;
    margin: 0 auto;
    padding: 0.3rem 0.4rem;
    background-color: transparent;
    font-family: inherit;
    font-size: inherit;
    text-align: left;
    border: none;
    cursor: pointer;
    outline: none;
    font-weight: 200;
    font-size: 1.25rem;
  }

  .breadcrumbs:disabled {
    cursor: default;
  }

  .node {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: white;
    overflow: hidden;
    pointer-events: all;
  }

  .node:not(.leaf) {
    cursor: pointer;
  }

  .contents {
    width: 100%;
    height: 100%;
    padding: 0.5rem;
    border: 1px solid white;
    border-radius: 4px;
    box-sizing: border-box;
    overflow: hidden;
    transition: opacity 500ms ease, border 500ms ease;
    position: relative;
  }

  .contents,
  .contents * {
    color: white;
  }

  /* .node:not(.leaf) .contents {
  } */

  /* .leaf .contents {
    opacity: 0.8;
  } */
  .has_children:hover .contents {
    opacity: 1;
  }

  h1 {
    font-size: 1.5rem;
    margin-bottom: 0.35rem;
    transition: opacity 400ms ease;
    font-weight: 700;
    text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.35);
    /* font-family: var(--font-serif); */
  }

  h2 {
    font-size: 1.25rem;
    font-weight: 200;
    transition: opacity 400ms ease;
  }

  .small h1,
  .small h2 {
    opacity: 0;
  }

  .small:hover h1,
  .small:hover h2 {
    opacity: 1 !important;
  }

  .scrolly.inactive {
    background-color: #b8b7bb !important;
  }

  .inactive:not(.explore) {
    filter: blur(1px);
  }

  .inactive:not(.explore):hover {
    filter: none;
  }

  .active {
    border: 2px solid black;
  }

  .active.explore {
    border: 1px solid white;
  }
  .active.explore::after {
    content: "\2605";
    position: absolute;
    bottom: 3px;
    right: 3px;
    font-size: 1rem;
    text-shadow: 1px 2px 3px rgba(0, 0, 0, 0.15);
    color: white;
  }

  .inactive:not(.explore) {
    background-color: #b8b7bb !important;
  }

  @media screen and (max-width: 568px) {
    .chart {
      height: calc(80vh - var(--nav-height));
    }

    .treemap {
      top: 12px;
    }
  }

  .breadcrumbs.clickable {
    animation: wiggle 0.7s linear forwards;
    -webkit-animation: wiggle 0.7s linear forwards;
  }

  @keyframes wiggle {
    0% {
      transform: translateX(0);
    }

    25% {
      transform: translateX(-5px);
    }

    50% {
      transform: translateX(0);
    }

    75% {
      transform: translateX(-5px);
    }

    100% {
      transform: translateX(0);
    }
  }

  .flex {
    display: flex;
    width: 100%;
    justify-content: space-evenly;
    place-items: center;
    overflow: hidden;
    max-width: 100vw;
  }
</style>
